import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import staffExtendedService, { PerformanceReview } from '../../services/staffExtendedService';

interface PerformanceReviewsProps {
  staffMembers: any[];
}

const PerformanceReviews: React.FC<PerformanceReviewsProps> = ({ staffMembers }) => {
  const [reviews, setReviews] = useState<PerformanceReview[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const { data } = await staffExtendedService.getReviews();
      setReviews(data);
    } catch (err) {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const getRatingColor = (rating?: number) => {
    if (!rating) return 'gray';
    if (rating >= 4) return 'green';
    if (rating >= 3) return 'yellow';
    return 'red';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-gray-800">Performance Reviews</h3>
        <button 
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          New Review
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 shadow">
          <p className="text-sm text-gray-600">Total Reviews</p>
          <p className="text-3xl font-bold">{reviews.length}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow">
          <p className="text-sm text-gray-600">Completed</p>
          <p className="text-3xl font-bold text-green-600">{reviews.filter(r => r.status === 'completed').length}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow">
          <p className="text-sm text-gray-600">In Progress</p>
          <p className="text-3xl font-bold text-yellow-600">{reviews.filter(r => r.status === 'submitted').length}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow">
          <p className="text-sm text-gray-600">Draft</p>
          <p className="text-3xl font-bold text-gray-600">{reviews.filter(r => r.status === 'draft').length}</p>
        </div>
      </div>

      {/* Reviews List */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        {loading ? (
          <p className="text-center text-gray-500">Loading...</p>
        ) : reviews.length === 0 ? (
          <p className="text-center text-gray-500">No reviews yet</p>
        ) : (
          <div className="space-y-4">
            {reviews.map(review => {
              const staff = staffMembers.find(s => s.id === review.staff_id);
              return (
                <div key={review.id} className="border rounded-lg p-4 hover:shadow-md transition">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-lg">
                        {staff?.first_name} {staff?.last_name}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {new Date(review.review_period_start).toLocaleDateString()} - {new Date(review.review_period_end).toLocaleDateString()}
                      </p>
                      <span className="inline-block mt-2 text-xs capitalize px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                        {review.review_type || 'annual'}
                      </span>
                    </div>
                    <div className="text-right">
                      {review.overall_rating && (
                        <div className={`text-2xl font-bold text-${getRatingColor(review.overall_rating)}-600`}>
                          {review.overall_rating}/5
                        </div>
                      )}
                      <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs ${
                        review.status === 'completed' ? 'bg-green-100 text-green-800' :
                        review.status === 'submitted' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {review.status}
                      </span>
                    </div>
                  </div>
                  {review.strengths && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-gray-700">Strengths:</p>
                      <p className="text-sm text-gray-600 line-clamp-2">{review.strengths}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">Create Performance Review</h3>
            <p className="text-gray-600 mb-4">Full review form interface - allows rating, feedback, goals, etc.</p>
            <button
              onClick={() => setShowCreateModal(false)}
              className="w-full bg-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceReviews;
